import { Injectable } from '@angular/core';
import { DayOfWeek } from '../classes/enums/DayOfWeek';
import { PreferredTimeOff } from '../classes/models/employee/PreferredTimeOff';
import { Availability } from '../classes/models/employee/Availability';

type TimeSlot = Availability | PreferredTimeOff;

/**
 * This service handles finding data for a specific week.
 */
@Injectable({
  providedIn: 'root'
})
export class WeekTimeSlotsService {

  /**
   * Finds data which would be applicable for the selected week by calculating the latest relevant start date. Then the data is grouped
   * by the {@link DayOfWeek}.
   * @param weekStart the Monday from which the week starts.
   * @param data a map from start date to either availability or preferred time off.
   */
  getWeekData<T extends TimeSlot>(weekStart: string, data: Map<string, T[]>): Map<DayOfWeek, T> {
    const startDate = this.getStartDateOfCurrentWeek(weekStart, data);
    if (startDate !== undefined) {
      return this.groupWeekdays(data.get(startDate));
    } else {
      return new Map<DayOfWeek, T>();
    }
  }

  private groupWeekdays<T extends TimeSlot>(slots: T[]): Map<DayOfWeek, T> {
    const weekdays: Map<DayOfWeek, T> = new Map();
    slots.forEach(slot => weekdays.set(slot.dayOfWeek, slot));
    return weekdays;
  }

  private getStartDateOfCurrentWeek<T extends TimeSlot>(weekStart: string, data: Map<string, T[]>): string {
    const filteredDates = Array.from(data.keys())
      .filter(startDate => startDate <= weekStart);
    if (filteredDates.length === 0) {
      return undefined;
    }
    return filteredDates.reduce((previous, current) => current > previous ? current : previous);
  }
}
